import { all, call, fork, put, select, takeLatest } from "typed-redux-saga";
import { Action, configuratorActions } from "./configurator-store";
import { errorActions } from "src/components/error-viewer/error-store";
import { ConfiguratorService, Model_LabelAndReferencePost, ProjectLabelService } from "src/backend/openapi/v1";
import { applicationActions } from "src/components/application-store";
import { RootState } from "src/store";
import { getSelectedAttributeIds } from "./configurator-utils";
import { hasAttributeFilter } from "./components/filter/filter-types";

export function* getConfigurations(action: ReturnType<typeof configuratorActions.getConfigurationsRequest>) {
  // The global bar is ref-counted (application-store.ts), so the decrement in `finally` is only correct
  // for a run that incremented it. On the skeleton path below there is nothing to take back, and a bare
  // decrement would clear a count another in-flight request is holding — hiding the bar out from under it.
  let raisedLoadingBar = false;

  try {
    const configurationsState = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState);
    const selectedItemIds = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied.selectedItemIds);
    const selectedAttributeIds = getSelectedAttributeIds(selectedItemIds);

    // Loading
    if (configurationsState) {
      raisedLoadingBar = true;
      yield put(applicationActions.setGlobalLoadingBar(true));
    } else {
      yield put(configuratorActions.setIsLoading(true));
    }

    // Deliberately unfiltered: the Configurations tab must show every configuration regardless of
    // what the Products-only Filter panel has applied. The Products tab fetches its own narrowed
    // copy via getProducts below.
    const result = yield* call(ConfiguratorService.postApiV1ConfiguratorConfigurations, action.payload.projectId, { selectedAttributeIds, attributes: [] });
    yield put(configuratorActions.getConfigurationsSuccess(result));

    // This unfiltered refresh doesn't touch productsState, so a mutation (delete/copy/edit) would
    // leave the Products tab stale — re-run its own fetch whenever a filter is in effect.
    const appliedFilter = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter);
    if (hasAttributeFilter(appliedFilter)) {
      yield put(configuratorActions.getProductsRequest(action.payload.projectId));
    }
  } catch (error) {
    yield put(errorActions.addError(error));
  } finally {
    if (raisedLoadingBar) yield put(applicationActions.setGlobalLoadingBar(false));
    // isLoading is a plain boolean, not a count, so clearing it unconditionally is free and guarantees
    // the skeleton goes away even on a path that never set it.
    yield put(configuratorActions.setIsLoading(false));
  }
}

export function* watchGetConfigurations() {
  yield takeLatest(Action.GetConfigurationsRequest, getConfigurations);
}

// Mirrors getConfigurations but sends appliedFilter, and writes to the Products tab's own slice
export function* getProducts(action: ReturnType<typeof configuratorActions.getProductsRequest>) {
  // Same ref-counting rule as getConfigurations above — only a run that raised the bar may lower it.
  let raisedLoadingBar = false;

  try {
    const productsState = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.productsState);
    const selectedItemIds = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied.selectedItemIds);
    const appliedFilter = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter);
    const selectedAttributeIds = getSelectedAttributeIds(selectedItemIds);

    // Same split as getConfigurations: skeleton on the first filtered fetch (the list is about to
    // change wholesale), unobtrusive top bar once there's already a filtered list on screen
    if (productsState) {
      raisedLoadingBar = true;
      yield put(applicationActions.setGlobalLoadingBar(true));
    } else {
      yield put(configuratorActions.setIsProductsLoading(true));
    }

    const result = yield* call(ConfiguratorService.postApiV1ConfiguratorConfigurations, action.payload.projectId, { selectedAttributeIds, attributes: appliedFilter.attributes || [] });
    yield put(configuratorActions.getProductsSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
  } finally {
    if (raisedLoadingBar) yield put(applicationActions.setGlobalLoadingBar(false));
    yield put(configuratorActions.setIsProductsLoading(false));
  }
}

export function* watchGetProducts() {
  yield takeLatest(Action.GetProductsRequest, getProducts);
}

export function* deleteProjectLineItems(action: ReturnType<typeof configuratorActions.deleteProjectLineItemsRequest>) {
  try {
    yield put(applicationActions.setGlobalLoading(true));
    yield* call(ConfiguratorService.postApiV1ConfiguratorSystemProductConfigurationsDelete, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchdeleteProjectLineItems() {
  yield takeLatest(Action.DeleteProjectLineItemsRequest, deleteProjectLineItems);
}

export function* copyConfiguration(action: ReturnType<typeof configuratorActions.copyConfigurationsRequest>) {
  try {
    yield put(applicationActions.setGlobalLoading(true));
    // Copies configuration headers directly (cascades to their items), rather than the
    // item-copy endpoint's approximation of duplicating just a configuration's root item.
    yield* call(ConfiguratorService.postApiV1ConfiguratorSystemProductConfigurationCopy, action.payload.blankValues, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchcopyConfiguration() {
  yield takeLatest(Action.CopyConfigurationRequest, copyConfiguration);
}

export function* copyProjectLineItems(action: ReturnType<typeof configuratorActions.copyProjectLineItemsRequest>) {
  try {
    yield put(applicationActions.setGlobalLoading(true));
    yield* call(ConfiguratorService.postApiV1ConfiguratorSystemProductConfigurationItemCopy, action.payload.blankValues, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchcopyProjectLineItems() {
  yield takeLatest(Action.CopyProjectLineItemsRequest, copyProjectLineItems);
}

export function* getLabels(action: ReturnType<typeof configuratorActions.getLabelsRequest>) {
  try {
    const result = yield* call(ProjectLabelService.getApiV1LabelProject, action.payload.projectId);
    yield put(configuratorActions.getLabelsSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchgetLabels() {
  yield takeLatest(Action.GetLabelsRequest, getLabels);
}

export function* getAttributesCatalog(action: ReturnType<typeof configuratorActions.getAttributesCatalogRequest>) {
  try {
    const result = yield* call(ConfiguratorService.getApiV1ConfiguratorConfigurationsAttributes, action.payload.projectId);
    yield put(configuratorActions.getAttributesCatalogSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(configuratorActions.getAttributesCatalogFail());
  }
}

export function* watchGetAttributesCatalog() {
  yield takeLatest(Action.GetAttributesCatalogRequest, getAttributesCatalog);
}

export function* postLabels(action: ReturnType<typeof configuratorActions.postLabelsRequest>) {
  try {
    const labels = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels || []);

    const selectedLabels = action.payload.labels.map(selectedLabel => {
      const foundLabel = labels.find(label => label.shortname === selectedLabel);
      return {
        labelId: foundLabel?.labelId || undefined,
        shortName: selectedLabel
      };
    });

    const payload: Model_LabelAndReferencePost = {
      entityIds: action.payload.entityIds,
      labels: selectedLabels
    };

    const result = yield* call(ProjectLabelService.postApiV1LabelProject, action.payload.projectId, payload);
    yield put(configuratorActions.getLabelsSuccess(result));
    action.payload.callback(true, result);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callback(false);
  }
}

export function* watchpostLabels() {
  yield takeLatest(Action.PostLabelsRequest, postLabels);
}

export function* deleteConfiguration(action: ReturnType<typeof configuratorActions.deleteConfigurationsRequest>) {
  try {
    yield put(applicationActions.setGlobalLoading(true));
    yield* call(ConfiguratorService.postApiV1ConfiguratorConfigurationDelete, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchdeleteConfiguration() {
  yield takeLatest(Action.DeleteConfigurationRequest, deleteConfiguration);
}

export function* configuratorSaga() {
  yield all([
    fork(watchGetConfigurations),
    fork(watchGetProducts),
    fork(watchdeleteProjectLineItems),
    fork(watchcopyConfiguration),
    fork(watchcopyProjectLineItems),
    fork(watchgetLabels),
    fork(watchpostLabels),
    fork(watchdeleteConfiguration),
    fork(watchGetAttributesCatalog)
  ]);
}
